import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../../supabaseClient';
import {
  Grid,
  TextField,
  Box,
  alpha,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Chip,
  Typography,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Gavel as GavelIcon,
  SaveAs as SaveAsIcon,
  FolderOpen as FolderOpenIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { SectionPaper, SectionTitle } from './styledComponents';

const PITermsConditionsSection = ({ purchaseOrder, onUpdate, canEdit = true, canDelete = true }) => {
  const theme = useTheme();
  const { companyId } = useParams();
  const [templates, setTemplates] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const defaultTerms = `PROFORMA INVOICE TERMS AND CONDITIONS:

1. ACCEPTANCE: This Proforma Invoice constitutes an offer and is subject to acceptance by Buyer. Acceptance is limited to the terms and conditions stated herein.

2. DELIVERY: Time is of the essence. Seller must deliver all products by the delivery date specified. Late delivery may result in order cancellation.

3. INSPECTION: All goods are subject to inspection and acceptance by Buyer after delivery. Buyer reserves the right to reject any non-conforming goods.

4. WARRANTY: Seller warrants that all products are free from defects in material and workmanship, merchantable, fit for their intended purpose, and conform to specifications.

5. PAYMENT TERMS: Payment shall be made according to the payment terms specified in this Proforma Invoice.

6. CHANGES: No changes, modifications, or amendments to this Proforma Invoice are valid unless made in writing and signed by authorized representatives of both parties.

7. RETURNS: Product returns must be authorized in advance and are subject to the return policy stated herein or agreed upon separately.

8. LIMITATION OF LIABILITY: Neither party shall be liable for indirect, incidental, special, or consequential damages arising from this Proforma Invoice.

9. GOVERNING LAW: This Proforma Invoice shall be governed by the laws of the applicable jurisdiction where the Seller's business is located.`;

  // Load templates
  useEffect(() => {
    if (companyId) {
      fetchTemplates();
    }
  }, [companyId]);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('terms_templates')
        .select('*')
        .eq('company_id', companyId)
        .in('template_type', ['po', 'both'])
        .order('template_name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleLoadTemplate = (template) => {
    onUpdate({ terms_and_conditions: template.terms_content });
    setLoadDialogOpen(false);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setSnackbar({ open: true, message: 'Please enter a template name', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('terms_templates')
        .insert([{
          company_id: companyId,
          template_name: templateName.trim(),
          template_type: 'both',
          terms_content: purchaseOrder.terms_and_conditions || '',
          created_by: user?.id
        }]);

      if (error) throw error;

      await fetchTemplates();
      setTemplateName('');
      setSaveDialogOpen(false);
      setSnackbar({ open: true, message: 'Template saved successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error saving template:', err);
      setSnackbar({ open: true, message: 'Failed to save template: ' + err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ 
      ...template, 
      editedName: template.template_name,
      editedContent: template.terms_content 
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate?.editedName?.trim()) {
      setSnackbar({ open: true, message: 'Please enter a template name', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('terms_templates')
        .update({
          template_name: editingTemplate.editedName.trim(),
          terms_content: editingTemplate.editedContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingTemplate.id);

      if (error) throw error;

      await fetchTemplates();
      setEditingTemplate(null);
      setEditDialogOpen(false);
      setSnackbar({ open: true, message: 'Template updated successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error updating template:', err);
      setSnackbar({ open: true, message: 'Failed to update template: ' + err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      const { error } = await supabase
        .from('terms_templates')
        .delete()
        .eq('id', templateToDelete.id);

      if (error) throw error;
      await fetchTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      setSnackbar({ open: true, message: 'Template deleted successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error deleting template:', err);
      setSnackbar({ open: true, message: 'Failed to delete template: ' + err.message, severity: 'error' });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  return (
    <SectionPaper sx={{ width: '100%', mt: 3 }}>
      <SectionTitle variant="h6">
        <GavelIcon />
        Terms & Conditions
      </SectionTitle>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(theme.palette.warning.main, 0.05), 
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            mb: 2
          }}>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'warning.main' }}>
              Important: These terms govern this proforma invoice. Modify as needed for your business requirements.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              onClick={() => canEdit && setLoadDialogOpen(true)}
              sx={{ 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                opacity: canEdit ? 1 : 0.3,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            >
              Load Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveAsIcon />}
              onClick={() => canEdit && setSaveDialogOpen(true)}
              sx={{ 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                opacity: canEdit ? 1 : 0.3,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            >
              Save as Template
            </Button>
          </Box>

          <TextField
            label="Terms and Conditions"
            fullWidth
            multiline
            rows={35}
            value={purchaseOrder.terms_and_conditions || ''}
            onChange={(e) => canEdit && onUpdate({ terms_and_conditions: e.target.value })}
            placeholder="Enter terms and conditions (optional)..."
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                opacity: canEdit ? 1 : 0.6,
                '& fieldset': { border: 'none' },
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }
            }}
          />
        </Grid>
      </Grid>

      {/* Load Template Dialog */}
      <Dialog 
        open={loadDialogOpen} 
        onClose={() => setLoadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Load Terms Template
          </Typography>
          <IconButton onClick={() => setLoadDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {templates.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No saved templates. Create one by clicking "Save as Template".
            </Typography>
          ) : (
            <List>
              {templates.map((template) => (
                <ListItem
                  key={template.id}
                  disablePadding
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        edge="end" 
                        onClick={() => canEdit && handleEditTemplate(template)}
                        color="primary"
                        sx={{
                          opacity: canEdit ? 1 : 0.3,
                          pointerEvents: canEdit ? 'auto' : 'none',
                          cursor: canEdit ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        onClick={() => canDelete && handleDeleteClick(template)}
                        color="error"
                        sx={{
                          opacity: canDelete ? 1 : 0.3,
                          pointerEvents: canDelete ? 'auto' : 'none',
                          cursor: canDelete ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemButton onClick={() => handleLoadTemplate(template)}>
                    <ListItemText
                      primary={template.template_name}
                      secondary={`Created ${new Date(template.created_at).toLocaleDateString()}`}
                    />
                    {template.is_default && (
                      <Chip label="Default" size="small" color="primary" sx={{ ml: 1 }} />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog 
        open={saveDialogOpen} 
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Save Terms Template
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Standard PI Terms, International Order Terms"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveTemplate} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => {
          setEditDialogOpen(false);
          setEditingTemplate(null);
        }}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Edit Terms Template
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={editingTemplate?.editedName || ''}
            onChange={(e) => setEditingTemplate({
              ...editingTemplate,
              editedName: e.target.value
            })}
            placeholder="e.g., Standard PI Terms, International Order Terms"
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            label="Template Content"
            multiline
            rows={20}
            fullWidth
            value={editingTemplate?.editedContent || ''}
            onChange={(e) => setEditingTemplate({
              ...editingTemplate,
              editedContent: e.target.value
            })}
            sx={{ 
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingTemplate(null);
          }}>Cancel</Button>
          <Button 
            onClick={handleUpdateTemplate} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? 'Updating...' : 'Update Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'error.main' }}>
          Delete Template?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{templateToDelete?.template_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setTemplateToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SectionPaper>
  );
};

export default PITermsConditionsSection;